import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';

import { MainLoader } from '@/_components';
import { useGetCurrentUser } from '@/_pages/auth/api';
import { CookieManager, useBoolean } from '@/_shared';
import { queryClient } from '@/_shared/utils/query-client';
import { useCurrentLocation } from '@/_shared/utils/route-utils';

import { AuthContext } from './context';
import { useAuthTokenListener } from './use-auth-token-listener';
import { useAuthValues } from './use-auth-values';
import { useLocalPermissions } from './use-local-permissions';
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isSuccess } = useGetCurrentUser();
  const { matched, location } = useCurrentLocation();
  const { pathname } = useLocation();
  const router = useRouter();
  const nav = useNavigate();

  const [isUserRestored, userRestored] = useBoolean(false);
  const { user, setUser, permissions, ...rest } = useAuthValues();
  const { getModelPermissionByPath, getPermissionModelDetailsByPath } = useLocalPermissions(permissions);

  const handleTokenChange = useCallback(
    (accessToken?: string) => {
      if (!isUserRestored) {
        return;
      }

      if (!matched) {
        return;
      }

      const token = accessToken?.trim() ? accessToken : undefined;
      const isPublicRoute = matched.id.indexOf('/_private') !== 0;

      if (!token) {
        if (!isPublicRoute) {
          setUser(null);
          void queryClient.removeQueries();
          nav({ to: '/login' });
        }
        return;
      }

      if (isPublicRoute) {
        nav({ to: '/dashboard' });
      }
    },
    [isUserRestored, matched, nav, setUser]
  );

  const onAuthenticated = useCallback(() => {
    handleTokenChange(CookieManager.getAccessToken());
  }, [handleTokenChange]);

  const onUnauthenticated = useCallback(() => {
    handleTokenChange(undefined);
  }, [handleTokenChange]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    setUser(data);
    userRestored.setTrue();
  }, [data, isSuccess, setUser, userRestored]);

  useAuthTokenListener({
    onAuthenticated,
    onUnauthenticated,
  });

  useEffect(() => {
    handleTokenChange(CookieManager.getAccessToken());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isUserRestored, user]);

  const permissionCtrl = useCallback(
    (path: string) => {
      if (!permissions || Object.keys(permissions).length === 0) {
        return;
      }

      const { modelPath, isModel, pathParts, sanitizedPath } = getPermissionModelDetailsByPath(path);

      const { model, tab } = permissions;
      const modelPermission = getModelPermissionByPath(modelPath);

      // handles all main pages
      if (isModel) {
        if (!modelPermission || !modelPermission.view) {
          console.log('Permission denied for model:', model, path);
          router.navigate({ to: '/dashboard' });
        }
        return;
      }

      // parent-new is an exception for inventory parent but linked with create permission
      // handle create new paths
      if (!isModel && (sanitizedPath.endsWith('/new') || sanitizedPath.endsWith('/parent-new'))) {
        if (!modelPermission || !modelPermission.create) {
          console.log('Permission denied for model create:', model, path);
          router.navigate({ to: '/dashboard' });
        }
        return;
      }

      // handle settings as special case
      if (!isModel && modelPermission?.resource === 'settings') {
        if (!modelPermission || !modelPermission.view) {
          console.log('Permission denied for Settings Tab:', model, path);
          router.navigate({ to: '/dashboard' });
        }
        return;
      }

      if (!modelPermission) {
        console.log('Tab parent route not found:', modelPermission);
        router.navigate({ to: '/dashboard' });
        return;
      }

      const tabPermissions = (tab ?? []).find(({ resource }) => {
        const resourceParts = resource.split('/');
        if (resourceParts.length < 2) {
          console.warn('invalid tab permission detected', resource);
          return false;
        }
        return resourceParts[1] === pathParts[2];
      });

      if (!tabPermissions || !tabPermissions.view) {
        console.log('Permission denied for tab:', tab, `${modelPermission.resource}/${pathParts[3]}`);
        router.navigate({ to: '/dashboard' });
        return;
      }
    },
    [getModelPermissionByPath, getPermissionModelDetailsByPath, permissions, router]
  );

  useEffect(() => {
    const unSub = router.subscribe('onBeforeLoad', (evt) => {
      permissionCtrl(evt.toLocation.pathname);
    });

    return () => {
      unSub();
    };
  }, [permissionCtrl, router]);

  useLayoutEffect(() => {
    permissionCtrl(pathname);
  }, [pathname, permissionCtrl, permissions]);

  const contextValue = useMemo(() => ({ ...rest, permissions, user, setUser }), [rest, permissions, user, setUser]);

  return <AuthContext value={contextValue}>{!(user || isUserRestored) ? <MainLoader /> : children}</AuthContext>;
}
